import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import {
  Truck, AlertTriangle, Clock, CalendarDays,
  ClipboardList, ChevronRight, CheckCircle, Wrench,
  Newspaper, Plus, Trash2, X, Megaphone, Info, Wrench as WrenchIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';

const S_BADGE = { offen:'badge-red', in_bearbeitung:'badge-yellow', erledigt:'badge-green' };
const S_LABEL = { offen:'Offen', in_bearbeitung:'In Bearbeitung', erledigt:'Erledigt' };
const S_DOT   = { offen:'var(--red)', in_bearbeitung:'var(--yellow)', erledigt:'var(--green)' };

const EV_EMOJI = { schuetzenfest:'🎯', veranstaltung:'🎉', toilettenanhaenger:'🚽', kuehlanhaenger:'❄️', ausschankwagen:'🍺', sonstiges:'📦' };
const evIcon = (t) => t==='tuev'?'🔧':t==='inspektion'?'🔩':(EV_EMOJI[t]||'📅');

const NEWS_TYPES = [
  { v:'info',    l:'Information', icon:'ℹ️', color:'var(--blue)',   bg:'var(--blue-bg)'   },
  { v:'update',  l:'Update',      icon:'🔧', color:'var(--green)',  bg:'var(--green-bg)'  },
  { v:'warn',    l:'Warnung',     icon:'⚠️', color:'var(--yellow)', bg:'var(--yellow-bg)' },
  { v:'success', l:'Erfolg',      icon:'✅', color:'var(--green)',  bg:'var(--green-bg)'  },
];

const EMPTY_NEWS = { title:'', content:'', type:'info', is_update:false, expires_hours:24 };

export default function Dashboard() {
  const { can, user }       = useAuth();
  const [tab, setTab]       = useState('overview'); // overview | news
  const [stats, setStats]   = useState(null);
  const [tuev, setTuev]     = useState([]);
  const [news, setNews]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newsLoading, setNL]    = useState(true);
  const [addNews, setAddNews]   = useState(false);
  const [newsForm, setNF]       = useState(EMPTY_NEWS);
  const [savingNews, setSN]     = useState(false);
  const nav = useNavigate();

  const canWriteNews = can('news_manage');

  const loadStats = useCallback(async () => {
    try {
      // Stats und TÜV parallel laden — TÜV-Fehler ignorieren
      const [statsRes, tuevRes] = await Promise.allSettled([
        api.get('/users/dashboard/stats'),
        api.get('/vehicles/alerts/tuev?days=730'),
      ]);

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      }

      if (tuevRes.status === 'fulfilled' && Array.isArray(tuevRes.value.data) && tuevRes.value.data.length > 0) {
        setTuev(tuevRes.value.data);
      } else if (statsRes.status === 'fulfilled' && Array.isArray(statsRes.value.data?.tuevVehicles)) {
        // Fallback aus stats
        setTuev(statsRes.value.data.tuevVehicles);
      } else {
        // Fallback: tuevVehicles direkt aus stats
        if (statsRes.status === 'fulfilled') {
          const tv = statsRes.value.data?.tuevVehicles;
          if (Array.isArray(tv)) setTuev(tv);
        }
      }
    } catch(e) {
      console.warn('Dashboard loadStats:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNews = useCallback(async () => {
    setNL(true);
    try {
      const { data } = await api.get('/news');
      setNews(Array.isArray(data) ? data : []);
    } catch {} finally { setNL(false); }
  }, []);

  useEffect(() => { loadStats(); loadNews(); }, []);

  const saveNews = async () => {
    if (!newsForm.title.trim()) { toast.error('Titel ist Pflicht'); return; }
    if (!newsForm.content.trim()) { toast.error('Inhalt ist Pflicht'); return; }
    setSN(true);
    try {
      await api.post('/news', newsForm);
      toast.success('News veröffentlicht');
      setAddNews(false);
      setNF(EMPTY_NEWS);
      loadNews();
    } catch(err) { toast.error(err.response?.data?.error || 'Fehler'); }
    finally { setSN(false); }
  };

  const deleteNews = async (id) => {
    if (!window.confirm('News löschen?')) return;
    try {
      await api.delete(`/news/${id}`);
      toast.success('Gelöscht');
      loadNews();
    } catch { toast.error('Fehler'); }
  };

  const today = new Date().toLocaleDateString('de-DE', { weekday:'long', day:'2-digit', month:'long', year:'numeric' });

  return (
    <Layout title="Dashboard">
      {/* Tabs */}
      <div className="seg" style={{ marginBottom:16 }}>
        <button className={`seg-btn ${tab==='overview'?'active':''}`} onClick={()=>setTab('overview')}>
          <Truck size={13}/> Übersicht
        </button>
        <button className={`seg-btn ${tab==='news'?'active':''}`} onClick={()=>setTab('news')}>
          <Newspaper size={13}/> News
          {news.length>0&&<span style={{marginLeft:4,background:'var(--accent)',color:'#fff',borderRadius:999,padding:'0 5px',fontSize:'.65rem',fontWeight:700}}>{news.length}</span>}
        </button>
      </div>

      {/* ── ÜBERSICHT TAB ── */}
      {tab === 'overview' && (
        <>
          {/* Hero */}
          <div style={{ background:'linear-gradient(135deg,#e63946,#c1121f)',borderRadius:14,padding:'18px 20px',marginBottom:16,display:'flex',alignItems:'center',justifyContent:'space-between',boxShadow:'0 4px 20px rgba(230,57,70,.3)' }}>
            <div>
              <div style={{ color:'rgba(255,255,255,.7)',fontSize:'.7rem',fontWeight:600,marginBottom:4 }}>{today}</div>
              <div style={{ color:'#fff',fontSize:'1.15rem',fontWeight:800,lineHeight:1.25 }}>Getränkefachhandel<br/>Gerhards</div>
              <div style={{ color:'rgba(255,255,255,.7)',fontSize:'.75rem',marginTop:4 }}>Fuhrparkverwaltung</div>
            </div>
            <img src="/logo.jpg" alt="" style={{ height:56,borderRadius:10,objectFit:'cover',opacity:.92,border:'2px solid rgba(255,255,255,.2)',flexShrink:0 }} onError={e=>e.target.style.display='none'}/>
          </div>

          {loading ? <div className="loading-box"><div className="spinner spinner-lg"/></div> : (
            <>
              {/* KPI Grid */}
              <div className="stat-grid" style={{ marginBottom:16 }}>
                {[
                  { icon:Truck,         label:'Fahrzeuge',      val:stats?.totalVehicles??0,     color:'var(--blue)',   bg:'var(--blue-bg)',   to:'/fuhrpark',  perm:'fuhrpark' },
                  { icon:AlertTriangle, label:'Offene Schäden', val:stats?.openDamages??0,       color:'var(--red)',    bg:'var(--red-bg)',    to:'/schaden',   perm:'schaden' },
                  { icon:Wrench,        label:'In Bearbeitung', val:stats?.inProgressDamages??0, color:'var(--yellow)', bg:'var(--yellow-bg)', to:'/schaden',   perm:'schaden' },
                  { icon:Clock,         label:'TÜV Termine',       val:tuev.length,                color:'var(--purple)', bg:'var(--purple-bg)', to:'/fuhrpark',  perm:'fuhrpark' },
                ].filter(k => can(k.perm)).map(({icon:Icon,label,val,color,bg,to}) => (
                  <div key={label} className="stat-card" onClick={()=>nav(to)}>
                    <div className="stat-icon" style={{background:bg}}><Icon size={17} color={color}/></div>
                    <div>
                      <div className="stat-val" style={{color}}>{val}</div>
                      <div className="stat-lbl">{label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* TÜV */}
              {can('fuhrpark') && tuev.length > 0 && (
                <div className="card" style={{ marginBottom:16 }}>
                  <div className="card-head">
                    <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                      <div className="stat-icon" style={{ width:28,height:28,borderRadius:8,background:'var(--purple-bg)' }}><Clock size={13} color="var(--purple)"/></div>
                      <span className="card-title">TÜV & Inspektion</span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>nav('/fuhrpark')}>Alle <ChevronRight size={12}/></button>
                  </div>
                  {tuev.slice(0,4).map(v => {
                    const days = Math.ceil((new Date(v.next_tuev)-new Date())/86400000);
                    return (
                      <div key={v.id} style={{ display:'flex',alignItems:'center',gap:10,padding:'11px 16px',borderBottom:'1px solid var(--border)' }}>
                        <div style={{ width:8,height:8,borderRadius:'50%',background:days<=30?'var(--red)':'var(--yellow)',flexShrink:0 }}/>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ fontWeight:600,fontSize:'.82rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                            {v.brand&&v.brand!=='Andere'?`${v.brand} ${v.model}`:v.model}
                          </div>
                          <div style={{ fontSize:'.72rem',color:'var(--text-muted)',fontFamily:'monospace' }}>{v.license_plate}</div>
                        </div>
                        <span className={`badge badge-${days<=30?'red':'yellow'}`}>{days<=0?'Abgelaufen':`${days} Tage`}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Packlisten */}
              {can('packliste') && stats?.packing && (
                <div className="card" style={{ marginBottom:16 }}>
                  <div className="card-head">
                    <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                      <div className="stat-icon" style={{ width:28,height:28,borderRadius:8,background:'var(--blue-bg)' }}><ClipboardList size={13} color="var(--blue)"/></div>
                      <span className="card-title">Packlisten</span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>nav('/packliste')}>Alle <ChevronRight size={12}/></button>
                  </div>
                  <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:1,background:'var(--border)' }}>
                    {[
                      {l:'Offen',   v:stats.packing.open??0,      c:'var(--red)',   bg:'var(--red-bg)'},
                      {l:'Aktiv',   v:stats.packing.inProgress??0,c:'var(--yellow)',bg:'var(--yellow-bg)'},
                      {l:'Erledigt',v:stats.packing.done??0,       c:'var(--green)', bg:'var(--green-bg)'},
                    ].map(({l,v,c,bg}) => (
                      <div key={l} style={{ background:'var(--bg-card)',padding:'12px 8px',textAlign:'center' }}>
                        <div style={{ fontSize:'1.4rem',fontWeight:800,color:c,lineHeight:1 }}>{v}</div>
                        <div style={{ fontSize:'.68rem',color:'var(--text-muted)',fontWeight:600,marginTop:3 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Schäden */}
              {can('schaden') && (
                <div className="card" style={{ marginBottom:16 }}>
                  <div className="card-head">
                    <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                      <div className="stat-icon" style={{ width:28,height:28,borderRadius:8,background:'var(--red-bg)' }}><AlertTriangle size={13} color="var(--red)"/></div>
                      <span className="card-title">Aktuelle Schäden</span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>nav('/schaden')}>Alle <ChevronRight size={12}/></button>
                  </div>
                  {!stats?.recentDamages?.length ? (
                    <div style={{ padding:'20px 16px',textAlign:'center',color:'var(--text-muted)',fontSize:'.8rem',display:'flex',alignItems:'center',justifyContent:'center',gap:8 }}>
                      <CheckCircle size={16} color="var(--green)"/> Keine offenen Schadensmeldungen
                    </div>
                  ) : stats.recentDamages.map((d,i) => (
                    <div key={d.id} onClick={()=>nav('/schaden')}
                      style={{ display:'flex',alignItems:'center',gap:10,padding:'11px 16px',borderBottom:i<stats.recentDamages.length-1?'1px solid var(--border)':'none',cursor:'pointer' }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'}
                      onMouseLeave={e=>e.currentTarget.style.background=''}>
                      <div style={{ width:7,height:7,borderRadius:'50%',background:S_DOT[d.status]||'#94a3b8',flexShrink:0 }}/>
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ fontWeight:600,fontSize:'.82rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{d.title}</div>
                        <div style={{ fontSize:'.7rem',color:'var(--text-muted)' }}>
                          {d.brand&&d.brand!=='Andere'?`${d.brand} `:''}
                          {d.model} · <span style={{fontFamily:'monospace'}}>{d.license_plate}</span>
                        </div>
                      </div>
                      <span className={`badge ${S_BADGE[d.status]||'badge-gray'}`}>{S_LABEL[d.status]}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Termine */}
              {can('kalender') && (
                <div className="card">
                  <div className="card-head">
                    <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                      <div className="stat-icon" style={{ width:28,height:28,borderRadius:8,background:'var(--green-bg)' }}><CalendarDays size={13} color="var(--green)"/></div>
                      <span className="card-title">Nächste Termine</span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>nav('/kalender')}>Kalender <ChevronRight size={12}/></button>
                  </div>
                  {!stats?.upcomingEvents?.length ? (
                    <div style={{ padding:'20px 16px',textAlign:'center',color:'var(--text-muted)',fontSize:'.8rem' }}>Keine anstehenden Termine</div>
                  ) : stats.upcomingEvents.map((e,i)=>(
                    <div key={e.id} style={{ display:'flex',alignItems:'center',gap:10,padding:'11px 16px',borderBottom:i<stats.upcomingEvents.length-1?'1px solid var(--border)':'none' }}>
                      <div style={{ width:34,height:34,borderRadius:9,background:(e.color||'#2563eb')+'22',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:'1.1rem' }}>
                        {evIcon(e.event_type)}
                      </div>
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ fontWeight:600,fontSize:'.82rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{e.title}</div>
                        <div style={{ fontSize:'.7rem',color:'var(--text-muted)' }}>
                          {new Date(e.start_date).toLocaleDateString('de-DE',{weekday:'short',day:'2-digit',month:'short'})}
                          {e.license_plate?` · ${e.license_plate}`:''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── NEWS TAB ── */}
      {tab === 'news' && (
        <>
          {/* News schreiben (nur mit Berechtigung) */}
          {canWriteNews && (
            <div style={{ marginBottom:16 }}>
              {!addNews ? (
                <button className="btn btn-primary" style={{ width:'100%',justifyContent:'center',padding:'12px' }} onClick={()=>setAddNews(true)}>
                  <Plus size={15}/> Neue News / Update veröffentlichen
                </button>
              ) : (
                <div className="card">
                  <div className="card-head">
                    <span className="card-title">Neue News</span>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setAddNews(false)}><X size={15}/></button>
                  </div>
                  <div style={{ padding:'16px', display:'flex',flexDirection:'column',gap:12 }}>
                    {/* Typ */}
                    <div className="f-group">
                      <label className="f-label">Typ</label>
                      <div style={{ display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:8 }}>
                        {NEWS_TYPES.map(t => (
                          <button key={t.v} type="button"
                            onClick={()=>setNF(p=>({...p,type:t.v,is_update:t.v==='update'}))}
                            style={{
                              padding:'8px 10px',borderRadius:8,border:'1.5px solid',
                              borderColor:newsForm.type===t.v?t.color:'var(--border)',
                              background:newsForm.type===t.v?t.bg:'var(--bg)',
                              cursor:'pointer',display:'flex',alignItems:'center',gap:7,
                              transition:'all 150ms',fontFamily:'inherit',
                            }}>
                            <span style={{fontSize:'1rem'}}>{t.icon}</span>
                            <span style={{fontSize:'.82rem',fontWeight:700,color:newsForm.type===t.v?t.color:'var(--text)'}}>{t.l}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="f-group">
                      <label className="f-label">Titel *</label>
                      <input className="f-input" value={newsForm.title} onChange={e=>setNF(p=>({...p,title:e.target.value}))} placeholder="Kurze Überschrift"/>
                    </div>
                    <div className="f-group">
                      <label className="f-label">Inhalt *</label>
                      <textarea className="f-input" value={newsForm.content} onChange={e=>setNF(p=>({...p,content:e.target.value}))} rows={4} placeholder={newsForm.type==='update'?'Was wurde verbessert oder gefixt?\n- Backup-System repariert\n- Unterschriften im PDF fixed':'Nachricht...'}/>
                    </div>

                    {/* Ablauf */}
                    <div className="f-group">
                      <label className="f-label">Automatisch löschen nach</label>
                      <select className="f-input" value={newsForm.is_update ? 24 : newsForm.expires_hours||0}
                        onChange={e => setNF(p=>({...p, expires_hours: parseInt(e.target.value), is_update: parseInt(e.target.value)===24&&p.type==='update'}))}>
                        <option value={0}>Nie (bleibt dauerhaft)</option>
                        <option value={24}>1 Tag (empfohlen für Updates)</option>
                        <option value={72}>3 Tage</option>
                        <option value={168}>1 Woche</option>
                      </select>
                    </div>

                    <div style={{ display:'flex',gap:10 }}>
                      <button className="btn btn-secondary" style={{flex:1,justifyContent:'center'}} onClick={()=>setAddNews(false)}>Abbrechen</button>
                      <button className="btn btn-primary" style={{flex:1,justifyContent:'center'}} onClick={saveNews} disabled={savingNews}>
                        {savingNews?<><span className="spinner" style={{width:14,height:14}}/>Sende...</>:<><Megaphone size={14}/> Veröffentlichen</>}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* News-Liste */}
          {newsLoading ? (
            <div className="loading-box"><div className="spinner spinner-lg"/></div>
          ) : news.length === 0 ? (
            <div className="card">
              <div className="empty-box">
                <Newspaper size={40}/>
                <h3>Keine News</h3>
                <p style={{fontSize:'.82rem',color:'var(--text-muted)'}}>
                  {canWriteNews ? 'Veröffentliche die erste News oben.' : 'Noch keine News vorhanden.'}
                </p>
              </div>
            </div>
          ) : (
            <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
              {news.map(n => {
                const nt = NEWS_TYPES.find(t=>t.v===n.type) || NEWS_TYPES[0];
                const timeAgo = (() => {
                  const diff = Date.now() - new Date(n.created_at).getTime();
                  const h = Math.floor(diff/3600000);
                  if (h < 1) return 'Gerade eben';
                  if (h < 24) return `Vor ${h} Stunde${h>1?'n':''}`;
                  const d = Math.floor(h/24);
                  return `Vor ${d} Tag${d>1?'en':''}`;
                })();
                const expiresIn = n.expires_at ? (() => {
                  const diff = new Date(n.expires_at).getTime() - Date.now();
                  if (diff <= 0) return null;
                  const h = Math.floor(diff/3600000);
                  if (h < 24) return `Läuft ab in ${h}h`;
                  return `Läuft ab in ${Math.floor(h/24)}d`;
                })() : null;

                return (
                  <div key={n.id} className="card" style={{ borderLeft:`4px solid ${nt.color}` }}>
                    <div style={{ padding:'14px 16px' }}>
                      <div style={{ display:'flex',alignItems:'flex-start',gap:10 }}>
                        <span style={{ fontSize:'1.3rem',flexShrink:0 }}>{nt.icon}</span>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ fontWeight:700,fontSize:'.92rem' }}>{n.title}</div>
                          <div style={{ fontSize:'.72rem',color:'var(--text-muted)',marginTop:2 }}>
                            {n.author_name} · {timeAgo}
                            {expiresIn && <span style={{marginLeft:6,color:'var(--yellow)'}}> · {expiresIn}</span>}
                          </div>
                        </div>
                        {canWriteNews && (
                          <button className="btn btn-ghost btn-icon btn-sm" style={{color:'var(--red)',flexShrink:0}} onClick={()=>deleteNews(n.id)}>
                            <Trash2 size={13}/>
                          </button>
                        )}
                      </div>
                      <div style={{ marginTop:10,fontSize:'.85rem',lineHeight:1.7,color:'var(--text)',whiteSpace:'pre-line' }}>
                        {n.content}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
